import { useState } from 'react'
import { CheckCircleIcon, XCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline'

export default function Quiz({ questions = [] }) {
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [selectedAnswer, setSelectedAnswer] = useState(null)
  const [showExplanation, setShowExplanation] = useState(false)
  const [score, setScore] = useState(0)
  const [completed, setCompleted] = useState(false)

  const question = questions[currentQuestion]

  const handleAnswerSelect = (answerIndex) => {
    if (showExplanation) return
    
    setSelectedAnswer(answerIndex)
    setShowExplanation(true)
    
    if (answerIndex === question?.answerIndex) {
      setScore(score + 1)
    }
  }

  const handleNext = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
      setSelectedAnswer(null)
      setShowExplanation(false)
    } else {
      setCompleted(true)
    }
  }

  const resetQuiz = () => {
    setCurrentQuestion(0)
    setSelectedAnswer(null)
    setShowExplanation(false)
    setScore(0)
    setCompleted(false)
  }

  if (!questions.length) {
    return (
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Knowledge Check</h2>
          <p className="text-lg text-neutral-600">Quiz questions will be loaded here.</p>
        </div>
      </section>
    )
  }

  if (completed) {
    return (
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold mb-6">Quiz Complete!</h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-2xl mb-4">
              Your Score: {score} out of {questions.length}
            </p>
            <p className="text-lg text-neutral-600 mb-6">
              {score === questions.length 
                ? "Excellent! You have a strong understanding of these concepts."
                : "Keep learning! Every step toward understanding matters."
              }
            </p>
            <button
              onClick={resetQuiz}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2"
            >
              Take Quiz Again
            </button>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-16">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-3xl font-bold">Knowledge Check</h2>
            <span className="text-lg text-neutral-600">
              {currentQuestion + 1} of {questions.length}
            </span>
          </div>
          <div className="w-full bg-neutral-200 rounded-full h-2">
            <div 
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              role="progressbar"
              aria-valuenow={currentQuestion + 1}
              aria-valuemax={questions.length}
              aria-label="Quiz progress"
            ></div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-xl font-semibold mb-6">{question?.question}</h3>
          
          <div className="space-y-3 mb-6">
            {question?.options.map((option, index) => {
              const isSelected = selectedAnswer === index
              const isCorrect = index === question.answerIndex
              const showResult = showExplanation
              
              let buttonClass = "w-full text-left p-4 rounded-lg border-2 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 "
              
              if (!showResult) {
                buttonClass += "border-neutral-200 hover:border-primary-300 hover:bg-primary-50"
              } else if (isCorrect) {
                buttonClass += "border-green-500 bg-green-50"
              } else if (isSelected && !isCorrect) {
                buttonClass += "border-red-500 bg-red-50"
              } else {
                buttonClass += "border-neutral-200 bg-neutral-50"
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  className={buttonClass}
                  disabled={showExplanation}
                  aria-label={`Option ${index + 1}: ${option}`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {showResult && isCorrect && (
                      <CheckCircleIcon className="w-6 h-6 text-green-600" aria-hidden="true" />
                    )}
                    {showResult && isSelected && !isCorrect && (
                      <XCircleIcon className="w-6 h-6 text-red-600" aria-hidden="true" />
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {showExplanation && (
            <div className="bg-neutral-50 rounded-lg p-6 mb-6">
              <h4 className="font-semibold mb-2">Explanation:</h4>
              <p className="text-neutral-700">{question?.explanation}</p>
            </div>
          )}

          {showExplanation && (
            <button
              onClick={handleNext}
              className="bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 inline-flex items-center gap-2"
            >
              {currentQuestion < questions.length - 1 ? 'Next Question' : 'Complete Quiz'}
              <ArrowRightIcon className="w-5 h-5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}